/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import {
    array,
    bool,
    func, object, shape, string,
} from 'prop-types';
import React, { useEffect, useState } from 'react';
import QueryString from 'qs';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setProductUnAvailable } from '../../../../../../state/ducks/App/App-Actions';
import FoodConfiguratorOptionUI from './FoodConfiguratorOptionsUI';

const FoodConfiguratorOptions = ({
    layout, handleSkuChange, product, selectedSku, renderSingleUI, levels, attributesWatchList, optionLabels, labels, availableSections,
}) => {
    const { skuAttributes } = layout;
    const location = useLocation();
    const params = QueryString.parse(location.search.replace(/^\?/, ''));

    const findSkuById = (skuId) => product?.productSkus?.find?.((item) => (
        item?.id === skuId || item?.id?.split('-')?.pop() === skuId
    )) || null;

    const [countValue, setCountValue] = useState(null);
    const [netWeightValue, setNetWeightValue] = useState(null);
    const [usdaValue, setUsdaValue] = useState(null);
    const [wineValue, setWineValue] = useState(null);

    const [preSelect, setPreSelect] = useState(null);
    const [countList, setCountList] = useState([]);
    const [netWeightList, setNetWeightList] = useState(null);
    const [usdaList, setUsdaList] = useState(null);
    const [wineList, setWineList] = useState(null);

    /**
     *
     * @param {*} key
     * @returns label from Label Handler file
     */
    const labelHandler = (key) => optionLabels[key] || labels?.[key] || (key || '').replace('usda_', '').replace('_', ' ');

    /**
     *
     * @param {*} key
     * @param {*} item
     * @param {*} sku
     * @param {*} disabled
     * @param {*} parent
     * @returns Option that will show for selection
     */
    const generateListValues = (key, item, sku, disabled, parent = null) => {
        const listValues = {
            disabled,
            type: key,
            item_id: item,
            label: labelHandler(item || ''),
            parent_item_id: parent,
            sku,

        };
        return listValues;
    };

    const actions = {
        count: { handleValue: setCountValue, handleList: setCountList, value: countValue },
        quality_grade: { handleValue: setUsdaValue, handleList: setUsdaList, value: usdaValue },
        net_weight: { handleValue: setNetWeightValue, handleList: setNetWeightList, value: netWeightValue },
        wine_beer_type: { handleValue: setWineValue, handleList: setWineList, value: wineValue },
    };

    /**
     *
     * @param {*} key
     * @returns Next section available for selection
     */
    const getNextAttributeWithAction = (key) => {
        const currentAttribute = (availableSections || []).indexOf(key);
        if (currentAttribute >= 0 && availableSections[currentAttribute + 1]) { // next
            return {
                name: availableSections[currentAttribute + 1],
                ...actions[availableSections[currentAttribute + 1]],
            };
        }
        return null;
    };

    /**
     *
     * @param {*} key
     * @returns Current attribute user clicked to get it's handler functions
     */
    const getAttributeWithAction = (key) => {
        const currentAttribute = (availableSections || []).indexOf(key);
        if (currentAttribute >= 0 && availableSections[currentAttribute]) { // next
            return {
                name: availableSections[currentAttribute],
                ...actions[availableSections[currentAttribute]],
            };
        }
        return null;
    };

    /**
     *
     * @param {*} option
     * @param {*} toFilter
     * @returns Filters to get depending attribute
     */
    const getFilters = (option, toFilter = []) => {
        const filters = [];
        if (countValue?.item_id && toFilter.includes('count')) {
            filters.push(option?.count === countValue.item_id);
        }
        if (netWeightValue?.item_id && toFilter.includes('net_weight')) {
            filters.push(option.net_weight === netWeightValue.item_id);
        }
        if (usdaValue?.item_id && toFilter.includes('quality_grade')) {
            filters.push(option.quality_grade === usdaValue.item_id);
        }
        if (wineValue?.item_id && toFilter.includes('wine_beer_type')) {
            filters.push(option.wine_beer_type === wineValue.item_id);
        }
        return filters;
    };

    useEffect(() => {
        if (typeof window === 'undefined') {
            return null;
        }
        window.foodConfiguratorLayout = true;
        return () => {
            window.foodConfiguratorLayout = undefined;
            window.foodConfiguratorMounted = false;
        };
    }, []);

    useEffect(() => {
        if (params?.selectSku) {
            setPreSelect(params?.selectSku);
        }
    }, []);

    /**
     * Run on page Load, based on fist available option list will generate
     */
    useEffect(() => {
        if (levels?.[0]?.name) {
            const { handleList, handleValue } = getAttributeWithAction(levels?.[0].name);
            const initList = layout?.default?.[levels[0].name]?.item?.map((item) => {
                const foundListItem = skuAttributes?.find?.((option) => option?.[levels[0].name] === item) || null;
                return generateListValues(levels?.[0]?.name, item, foundListItem?.sku, false);
            });
            (() => {
                /**
                 * logic to handle if there is any param in URL
                 * example URL: /v/wild-salmon/smoked-salmon-and-lox/FRL006?selectSku=1031-60369H
                 * This will select the 1031-60369H if it's available and is in list
                 */
                if (params?.selectSku) {
                    const foundInSkuList = findSkuById(params?.selectSku);
                    if (foundInSkuList) {
                        const getCount = foundInSkuList?.attributes?.find?.((item) => item?.name === 'count');
                        handleSkuChange({ target: { value: params?.selectSku } }, undefined, undefined, false);
                        handleValue(initList?.find((item) => item.sku === foundInSkuList.id || item.item_id === getCount?.values?.[0]?.value));
                        handleList(initList);
                    } else {
                        if (initList?.[0]?.sku) {
                            handleSkuChange({ target: { value: initList?.[0]?.sku } }, undefined, undefined, false);
                        }
                        handleValue(initList?.[0]);
                        handleList(initList);
                    }
                } else {
                    if (initList?.[0]?.sku) {
                        handleSkuChange({ target: { value: initList?.[0]?.sku } }, undefined, undefined, false);
                    }
                    handleValue(initList?.[0]);
                    handleList(initList);
                }
            })(); // setting in one function
        }
    }, []);

    useEffect(() => {
        if (levels?.[0]?.name && preSelect) {
            const { handleList, handleValue } = getAttributeWithAction(levels?.[0].name);
            const initList = layout?.default?.[levels[0].name]?.item?.map((item) => {
                const foundListItem = skuAttributes?.find?.((option) => option?.[levels[0].name] === item) || null;
                return generateListValues(levels?.[0]?.name, item, foundListItem?.sku, false);
            });
            (() => {
                /**
                 * logic to handle if there is any param in URL
                 * example URL: /v/wild-salmon/smoked-salmon-and-lox/FRL006?selectSku=1031-60369H
                 * This will select the 1031-60369H if it's available and is in list
                 */
                if (preSelect) {
                    const foundInSkuList = findSkuById(preSelect);
                    if (foundInSkuList) {
                        const getCount = foundInSkuList?.attributes?.find?.((item) => item?.name === 'count');
                        handleSkuChange({ target: { value: preSelect } }, undefined, undefined, false);
                        handleValue(initList?.find((item) => item.sku === foundInSkuList.id || item.item_id === getCount?.values?.[0]?.value));
                        handleList(initList);
                    }
                }
            })(); // setting in one function
        }
    }, [preSelect]);

    /**
     * If attribute Count changed the next attribute will be get either Net weight or USDA or Wine
     */
    useEffect(() => {
        const nextAttribute = getNextAttributeWithAction('count');
        let nextValueListMap = [];
        if (nextAttribute && countValue?.item_id) {
            nextValueListMap = layout?.default?.[nextAttribute?.name]?.item?.map((item) => {
                const foundListItem = skuAttributes?.find?.((option) => (option?.[nextAttribute?.name] === item && option.count === countValue?.item_id)) || null;
                return generateListValues(nextAttribute?.name, item, foundListItem?.sku, !foundListItem, countValue?.item_id);
            });
        }

        (() => {
            if (nextAttribute) {
                (() => {
                    const foundInSkuList = findSkuById(preSelect);
                    const getNextAttribute = foundInSkuList?.attributes?.find?.((item) => item?.name === nextAttribute?.name);
                    let findThatEnabled = preSelect ? nextValueListMap?.find?.((item) => item.sku === preSelect || item.item_id === getNextAttribute?.values?.[0]?.value) : null;
                    if (!findThatEnabled) {
                        findThatEnabled = nextValueListMap?.find?.((item) => !item.disabled);
                    }

                    const previousSelected = nextValueListMap?.find?.((item) => (item?.label === actions?.[nextAttribute?.name]?.value?.label && !item?.disabled));
                    if (previousSelected) {
                        findThatEnabled = previousSelected;
                    }
                    nextAttribute.handleValue(findThatEnabled);
                    nextAttribute.handleList(nextValueListMap);
                    if (findThatEnabled?.sku) {
                        handleSkuChange({ target: { value: findThatEnabled?.sku } }, undefined, undefined, !preSelect);
                    }
                })();
            } else if (countValue?.sku) {
                handleSkuChange({ target: { value: countValue.sku } }, undefined, undefined, !preSelect);
            }
        })(); // setting/calling in one function
    }, [countValue?.item_id]);

    /**
    * If attribute Net Weight changed the next attribute will be get either USDA or Wine
    */
    useEffect(() => {
        // USDA or Wine
        const nextAttribute = getNextAttributeWithAction('net_weight');
        let nextValueListMap = [];
        if (nextAttribute && (netWeightValue?.item_id || countValue?.item_id)) {
            nextValueListMap = layout?.default?.[nextAttribute?.name]?.item?.map((item) => {
                const foundListItem = skuAttributes?.find?.((option) => {
                    const allFilters = getFilters(option, ['count', 'net_weight']);
                    return (option?.[nextAttribute?.name] === item && allFilters.every(((filterItem) => filterItem === true)));
                }) || null;
                return generateListValues(nextAttribute?.name, item, foundListItem?.sku, !foundListItem, netWeightValue?.item_id || countValue?.item_id);
            });
        }

        (() => {
            if (nextAttribute) {
                (() => {
                    const foundInSkuList = findSkuById(preSelect);
                    const getNextAttribute = foundInSkuList?.attributes?.find?.((item) => item?.name === nextAttribute?.name);
                    let findThatEnabled = preSelect ? nextValueListMap?.find?.((item) => item.sku === preSelect || item.item_id === getNextAttribute?.values?.[0]?.value) : null;
                    if (!findThatEnabled) {
                        findThatEnabled = nextValueListMap?.find?.((item) => !item.disabled);
                    }
                    const previousSelected = nextValueListMap?.find?.((item) => (item?.label === actions?.[nextAttribute?.name]?.value?.label && !item?.disabled));
                    if (previousSelected) {
                        findThatEnabled = previousSelected;
                    }

                    nextAttribute.handleValue(findThatEnabled);
                    nextAttribute.handleList(nextValueListMap);
                    if (findThatEnabled?.sku) {
                        handleSkuChange({ target: { value: findThatEnabled?.sku } }, undefined, undefined, !preSelect);
                    }
                })();
            } else if (netWeightValue?.sku) {
                handleSkuChange({ target: { value: netWeightValue.sku } }, undefined, undefined, !preSelect);
            }
        })(); // setting/calling in one function
    }, [netWeightValue?.item_id]);

    /**
    * If attribute Quality grade/ USDA changed the next attribute will be get Wine
    */
    useEffect(() => {
        // Wine
        const nextAttribute = getNextAttributeWithAction('quality_grade');
        let nextValueListMap = [];
        if (nextAttribute && (netWeightValue?.item_id || countValue?.item_id)) {
            nextValueListMap = layout?.default?.[nextAttribute?.name]?.item?.map((item) => {
                const foundListItem = skuAttributes?.find?.((option) => {
                    const allFilters = getFilters(option, ['count', 'net_weight', 'quality_grade']);
                    return (option?.[nextAttribute?.name] === item && allFilters.every(((filterItem) => filterItem === true)));
                }) || null;
                return generateListValues(nextAttribute?.name, item, foundListItem?.sku, !foundListItem, usdaValue?.item_id || netWeightValue?.item_id || countValue?.item_id);
            });
        }

        (() => {
            if (nextAttribute) {
                (() => {
                    const foundInSkuList = findSkuById(preSelect);
                    const getNextAttribute = foundInSkuList?.attributes?.find?.((item) => item?.name === nextAttribute?.name);
                    let findThatEnabled = preSelect ? nextValueListMap?.find?.((item) => item.sku === preSelect || item.item_id === getNextAttribute?.values?.[0]?.value) : null;
                    if (!findThatEnabled) {
                        findThatEnabled = nextValueListMap?.find?.((item) => !item.disabled);
                    }
                    const previousSelected = nextValueListMap?.find?.((item) => (item?.label === actions?.[nextAttribute?.name]?.value?.label && !item?.disabled));
                    if (previousSelected) {
                        findThatEnabled = previousSelected;
                    }
                    nextAttribute.handleValue(findThatEnabled);
                    nextAttribute.handleList(nextValueListMap);
                    if (findThatEnabled?.sku) {
                        handleSkuChange({ target: { value: findThatEnabled?.sku } }, undefined, undefined, !preSelect);
                    }
                })();
            } else if (usdaValue?.sku) {
                handleSkuChange({ target: { value: usdaValue.sku } }, undefined, undefined, !preSelect);
            }
        })(); // setting/calling in one function
    }, [usdaValue?.item_id]);

    /**
     * Wine changed? no next attribute just selected the SKU
     */
    useEffect(() => {
        if (wineValue?.sku) {
            handleSkuChange({ target: { value: wineValue.sku } }, undefined, undefined, !preSelect);
        }
    }, [wineValue?.item_id]);

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(setProductUnAvailable(false));
    }, []);

    return (
        <FoodConfiguratorOptionUI
            product={product}
            selectedSku={selectedSku}
            renderSingleUI={renderSingleUI}
            levels={levels}
            attributesWatchList={attributesWatchList}
            attributesList={{
                count: { list: countList, totalItems: countList?.length, label: labelHandler('count') },
                net_weight: { list: netWeightList, totalItems: netWeightList?.length, label: labelHandler('net_weight') },
                quality_grade: { list: usdaList, totalItems: usdaList?.length, label: labelHandler('quality_grade') },
                wine_beer_type: { list: wineList, totalItems: wineList?.length, label: labelHandler('wine_beer_type') },
            }}
            attributesValue={{
                count: countValue,
                net_weight: netWeightValue,
                quality_grade: usdaValue,
                wine_beer_type: wineValue,
            }}
            setValue={{
                count: setCountValue,
                net_weight: setNetWeightValue,
                quality_grade: setUsdaValue,
                wine_beer_type: setWineValue,
            }}
            setPreSelect={(val) => {
                if (preSelect) {
                    setPreSelect(val);
                }
            }}
        />
    );
};

FoodConfiguratorOptions.propTypes = {
    layout: shape({
        default: object,
    }),
    handleSkuChange: func,
    selectedSku: shape({
        id: string,
    }),
    product: shape({
        name: string,
    }),
    renderSingleUI: bool,
    levels: array,
    attributesWatchList: array,
    optionLabels: object,
    labels: object,
    availableSections: array,
};
FoodConfiguratorOptions.defaultProps = {
    layout: {
        default: {},
    },
    selectedSku: {},
    product: {},
    handleSkuChange: () => { },
    renderSingleUI: false,
    levels: [],
    attributesWatchList: [],
    optionLabels: {},
    labels: {},
    availableSections: [],
};

export default FoodConfiguratorOptions;
